import React from 'react';
import { useForm } from 'react-hook-form';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

const CompleteProfileModal = () => {
  const {
    profileCompletionOpen,
    profileDraft,
    missingFields,
    completeProfile,
    closeProfileCompletion,
    loading
  } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      fullName: profileDraft?.fullName || '',
      phone: profileDraft?.phone || '',
      address: profileDraft?.address || ''
    }
  });

  React.useEffect(() => {
    reset({
      fullName: profileDraft?.fullName || '',
      phone: profileDraft?.phone || '',
      address: profileDraft?.address || ''
    });
  }, [profileDraft, reset]);

  const onSubmit = async (data) => {
    await completeProfile({
      fullName: data.fullName?.trim(),
      phone: data.phone?.trim(),
      address: data.address?.trim()
    });
    reset();
  };

  return (
    <AnimatePresence>
      {profileCompletionOpen && !loading && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white w-full max-w-md mx-auto p-6 shadow-xl"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
          >
            <div className="mb-4">
              <h3 className="text-2xl font-serif text-deep-brown">Complete your profile</h3>
              <p className="text-sm text-warm-gray mt-1">
                Please provide the missing details to finish setting up your account.
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-deep-brown mb-2">
                  Full Name {missingFields.fullName ? '*' : '(optional)'}
                </label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="Enter your full name"
                  {...register('fullName', {
                    required: missingFields.fullName ? 'Full name is required' : false,
                    minLength: missingFields.fullName
                      ? { value: 2, message: 'Name must be at least 2 characters' }
                      : undefined
                  })}
                />
                {errors.fullName && (
                  <p className="mt-1 text-sm text-red-600">{errors.fullName.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-deep-brown mb-2">
                  Phone Number {missingFields.phone ? '*' : '(optional)'}
                </label>
                <input
                  type="tel"
                  className="input-field"
                  placeholder="10-digit phone"
                  {...register('phone', {
                    required: missingFields.phone ? 'Phone number is required' : false,
                    pattern: missingFields.phone
                      ? {
                          value: /^[6-9]\d{9}$/,
                          message: 'Please enter a valid 10-digit phone number'
                        }
                      : undefined
                  })}
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-deep-brown mb-2">
                  Address (optional)
                </label>
                <textarea
                  rows={3}
                  className="input-field resize-none"
                  placeholder="Enter your address (optional)"
                  {...register('address')}
                />
              </div>

              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  className="py-2 px-4 border border-warm-gray/30 text-deep-brown hover:bg-gray-50"
                  onClick={closeProfileCompletion}
                >
                  Maybe later
                </button>
                <motion.button
                  type="submit"
                  className="btn-primary"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Save details
                </motion.button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CompleteProfileModal;
